import { createContext, useContext, useEffect, useState } from 'react';

import { useAppContext } from '@/context';
import { getAuthFromStorage } from '@/utils/auth';
import { ensureLanguageConsistency } from '@/utils/languages';
import { setBugsnagUser } from '@/utils/bugsnag';

const AuthContext = createContext(undefined as TypeAny);

function useProvideAuth() {
  const [authData, setAuthData] = useState(undefined as TypeAny);
  const [isInitializing, setIsInitializing] = useState(true);
  const { setAlert } = useAppContext();

  useEffect(() => {
    const authStorage = getAuthFromStorage();

    if (authStorage.token) {
      setAuthData(authStorage);
      // Initialize Bugsnag user if auth data exists
      if (authStorage.user) {
        setBugsnagUser(authStorage.user);
      }
    }

    // Mark initialization as complete
    setIsInitializing(false);
  }, []);

  // Ensure language consistency when user data changes
  useEffect(() => {
    if (authData?.user && !isInitializing) {
      ensureLanguageConsistency();
    }
  }, [authData?.user, isInitializing]);

  return {
    authData,
    setAuthData,
    isInitializing,
    setAlert
  };
}

interface ProvideAuthProps {
  children: React.ReactNode;
}

export function ProvideAuth({ children }: ProvideAuthProps) {
  const auth = useProvideAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext) || {};
export const useAuthUser = () => (useAuth()?.authData?.user as Api.UserObject) || {};
export const useAuthToken = () => (useAuth()?.authData?.token as string) || '';
export const useAuthInitializing = () => useAuth()?.isInitializing || false;
